export const ACCESS_PAGES = [
  { key: 'dashboard', label: 'Dashboard', path: '/dashboard' },
  { key: 'employees', label: 'Employees', path: '/employees' },
  { key: 'inventory', label: 'Inventory', path: '/inventory' },
  { key: 'vendors', label: 'Vendors', path: '/vendors' },
  { key: 'customers', label: 'Customers', path: '/customers' },
  { key: 'payments', label: 'Payments', path: '/payments' },
  { key: 'locations', label: 'Locations', path: '/locations' },
  { key: 'transfers', label: 'Transfers', path: '/transfers' },
  { key: 'sales', label: 'Sales', path: '/sales' },
  { key: 'reports', label: 'Reports', path: '/reports' },
  { key: 'repair-orders', label: 'Repairs', path: '/repair-orders' },
  { key: 'service-room', label: 'Service Room', path: '/service-room' },
  { key: 'customer-returns', label: 'Customer Returns', path: '/customer-returns' },
  { key: 'invoices', label: 'Invoices', path: '/invoices' },
  { key: 'orders', label: 'Orders', path: '/orders' },
  { key: 'services', label: 'Services', path: '/services' },
  { key: 'timesheets', label: 'Timesheets', path: '/timesheets' },
  { key: 'forms', label: 'Forms', path: '/forms' },
  { key: 'email-settings', label: 'Email Settings', path: '/email-settings' },
];

export const AUTHORITY_LEVELS = ['none', 'read', 'full', 'elevated'];

const AUTHORITY_RANK = {
  none: 0,
  read: 1,
  full: 2,
  elevated: 3,
};

const PAGE_READ_PERMISSIONS = {
  customers: 'customers.read',
  payments: 'payments.read',
  invoices: 'invoices.read',
  sales: 'sales.read',
  'customer-returns': 'returns.read',
  'repair-orders': 'repairs.read',
  inventory: 'inventory.read',
  transfers: 'transfers.read',
  locations: 'locations.read',
};

const ROLE_ROUTE_AUTHORITY = {
  admin: {
    dashboard: 'full',
    employees: 'full',
    inventory: 'full',
    vendors: 'full',
    customers: 'full',
    payments: 'full',
    locations: 'full',
    transfers: 'full',
    sales: 'full',
    'repair-orders': 'full',
    'service-room': 'full',
    'customer-returns': 'full',
    invoices: 'full',
    orders: 'full',
    services: 'full',
    timesheets: 'full',
    forms: 'full',
    'email-settings': 'full',
  },
  super: Object.fromEntries(ACCESS_PAGES.map((page) => [page.key, 'elevated'])),
  employee: {
    orders: 'full',
    inventory: 'full',
    vendors: 'full',
    customers: 'full',
    payments: 'full',
    locations: 'full',
    transfers: 'full',
    sales: 'full',
    'repair-orders': 'full',
    'service-room': 'full',
    'customer-returns': 'full',
    invoices: 'full',
  },
  employee_la: {
    orders: 'read',
  },
  factory: {
    services: 'full',
    'service-room': 'full',
  },
  technician: {
    'service-room': 'read',
  },
  payroll_manager: {
    employees: 'full',
    timesheets: 'full',
  },
  payroll_user: {
    timesheets: 'read',
  },
  payroll: {
    timesheets: 'read',
  },
};

export const normalizeAuthority = (value) => {
  const authority = String(value || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(AUTHORITY_RANK, authority) ? authority : 'none';
};

export const getPageAuthority = (user, pageKey) => {
  if (!user) return 'none';
  const role = String(user.role || '').toLowerCase();
  const roleAuthority = normalizeAuthority(ROLE_ROUTE_AUTHORITY[role]?.[pageKey]);
  if (pageKey === 'reports') {
    if (role === 'admin') return 'full';
    if (user.accessGroupId && Object.prototype.hasOwnProperty.call(user.accessPermissions || {}, 'reports')) return normalizeAuthority(user.accessPermissions.reports);
    return getPageAuthority(user, 'customers');
  }
  if (role === 'admin') return roleAuthority;
  if (user.accessGroupId) {
    const legacyAuthority = normalizeAuthority(user.accessPermissions?.[pageKey]);
    if (legacyAuthority !== 'none') return legacyAuthority;
    return hasPermission(user, PAGE_READ_PERMISSIONS[pageKey]) ? 'read' : 'none';
  }
  return roleAuthority;
};

export const hasPermission = (user, permissionKey) => {
  const permission = String(permissionKey || '').trim().toLowerCase();
  if (!user || !permission) return false;
  if (String(user.role || '').trim().toLowerCase() === 'admin') return true;

  const value = user.accessActionPermissions?.[permission];
  return value === true || Boolean(value && typeof value === 'object' && value.granted !== false);
};

const permissionScopes = (user, permissionKey) => {
  const permission = String(permissionKey || '').trim().toLowerCase();
  const value = user?.accessActionPermissions?.[permission];
  if (value === true) return [];
  if (!value || typeof value !== 'object' || value.granted === false) return null;
  return (Array.isArray(value.scopes) ? value.scopes : [value.scope])
    .map((scope) => String(scope || '').trim().toLowerCase())
    .filter(Boolean);
};

const isRetailResource = (resource) => [
  resource?.customerTypeName,
  resource?.customer_type_name,
  resource?.typeName,
  resource?.type_name,
  resource?.typeLabel,
  resource?.type_label,
  resource?.customerType,
  resource?.customer_type,
  resource?.cust_type,
  resource?.nc_customer_type,
  resource?.type,
].some((type) => String(type || '').trim().toLowerCase() === 'retail');

const normalizeDocumentType = (resource) => {
  const transactionTypeId = Number(resource?.transactionTypeId ?? resource?.transaction_type_id);
  if (transactionTypeId === 640) return 'repair';
  if (transactionTypeId === 10) return 'credit_memo';
  if (transactionTypeId === 4) return 'invoice';
  const documentId = resource?.documentId || resource?.document_id || resource?.id || resource?.invoiceNo || resource?.invoice_no;
  if (/^RO-/i.test(String(documentId || '').trim())) return 'repair';
  const type = String(resource?.documentType || resource?.document_type || resource?.type || '').trim().toLowerCase();
  if (['repair', 'repair_order', 'repair-order', 'repair order'].includes(type)) return 'repair';
  if (['invoice', 'shipping', 'sale'].includes(type)) return 'invoice';
  if (['credit_memo', 'credit-memo', 'credit memo'].includes(type)) return 'credit_memo';
  return type;
};

const RULE_FIELD_READERS = {
  'document.repair_only': resource => [isRepairOnlyResource(resource)],
  'customer.type': (resource) => [
    resource?.customerTypeName,
    resource?.customer_type_name,
    resource?.typeName,
    resource?.type_name,
    resource?.typeLabel,
    resource?.type_label,
    resource?.customerType,
    resource?.customer_type,
    resource?.cust_type,
    resource?.nc_customer_type,
    resource?.type,
  ],
  'location.active': (resource) => [
    resource?.active,
    resource?.isActive,
    resource?.is_active,
    resource?.loc_active,
  ],
};

const normalizeComparable = (value) => String(value ?? '').trim().toLowerCase();

const boolComparable = (value) => {
  if (typeof value === 'boolean') return value;
  const text = normalizeComparable(value);
  if (['true', '1', 'yes', 'y', 'active'].includes(text)) return true;
  if (['false', '0', 'no', 'n', 'inactive'].includes(text)) return false;
  return null;
};

const structuredRuleAllows = (rule, resource) => {
  if (!rule || typeof rule !== 'object') return false;
  const fieldKey = String(rule.fieldKey || rule.field_key || '').trim().toLowerCase();
  const operator = String(rule.operator || '').trim().toLowerCase();
  const expected = rule.comparisonValue ?? rule.comparison_value ?? rule.value;
  const values = RULE_FIELD_READERS[fieldKey]?.(resource) || [];

  if (operator === 'is_empty') return values.every((value) => String(value ?? '').trim() === '');
  if (operator === 'not_empty') return values.some((value) => String(value ?? '').trim() !== '');
  if (operator === 'is_true') return values.some((value) => boolComparable(value) === true);
  if (operator === 'is_false') return values.some((value) => boolComparable(value) === false);
  const populatedValues = values.filter((value) => String(value ?? '').trim() !== '');
  if (operator === 'in') {
    const allowed = String(expected || '').split(',').map(normalizeComparable);
    return populatedValues.some(value => allowed.includes(normalizeComparable(value)));
  }
  if (operator === 'equals') return populatedValues.some((value) => normalizeComparable(value) === normalizeComparable(expected));
  if (operator === 'not_equals') {
    return populatedValues.length > 0
      && populatedValues.every((value) => normalizeComparable(value) !== normalizeComparable(expected));
  }
  return false;
};

const specialRuleForScope = (user, scope) => {
  const rules = user?.accessScopeRules || user?.specialRules || {};
  if (Array.isArray(rules)) {
    return rules.find((rule) => String(rule?.scopeKey || rule?.scope_key || rule?.key || '').trim().toLowerCase() === scope);
  }
  return rules?.[scope] || null;
};

const isRepairOnlyResource = resource => {
    const allocations = Array.isArray(resource?.allocations)
      ? resource.allocations
      : Array.isArray(resource?.payments)
        ? resource.payments.flatMap((payment) => payment?.allocations || [])
        : null;
    if (allocations) return allocations.length === 0 || allocations.every((allocation) => normalizeDocumentType(allocation) === 'repair');
    return normalizeDocumentType(resource) === 'repair';
};

const scopeAllows = (user, scope, resource) => {
  const rule = specialRuleForScope(user, scope);
  if (rule) return structuredRuleAllows(rule, resource);
  if (scope === 'retail_only') return isRetailResource(resource);
  if (scope === 'repair_only') return isRepairOnlyResource(resource);
  return false;
};

export const can = (user, permissionKey, resource = null) => {
  const permission = String(permissionKey || '').trim().toLowerCase();
  if (!user || !permission) return false;
  if (String(user.role || '').trim().toLowerCase() === 'admin') return true;

  const scopes = permissionScopes(user, permission);
  if (!scopes) return false;
  if (scopes.length === 0) return true;
  return scopes.every((scope) => scopeAllows(user, scope, resource));
};

export const hasPageAuthority = (user, pageKey, minimumAuthority = 'read') => (
  AUTHORITY_RANK[getPageAuthority(user, pageKey)] >= AUTHORITY_RANK[normalizeAuthority(minimumAuthority)]
);

export const defaultRouteForUser = (user) => {
  const page = ACCESS_PAGES.find((entry) => hasPageAuthority(user, entry.key, 'read'));
  return page?.path || '/unauthorized';
};

export const pageKeyForPath = (pathname) => {
  if (pathname === '/reports' || pathname.startsWith('/reports/')) return 'reports';
  const match = ACCESS_PAGES
    .filter((page) => pathname === page.path || pathname.startsWith(`${page.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return match?.key || null;
};
